import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { UsersRound, Loader2, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuthors() {
  const { authFetch } = useAdmin();
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/authors`);
    if (r.ok) setAuthors((await r.json()).authors || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const empty = { name: '', slug: '', email: '', picture: '', bio: '', role: 'Autor', visible: true };

  const save = async () => {
    if (!editing?.name) return toast.error('Ime je obavezno');
    const method = editing.id ? 'PUT' : 'POST';
    const url = editing.id
      ? `${API}/admin/authors/${editing.id}`
      : `${API}/admin/authors`;
    const r = await authFetch(url, {
      method, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editing),
    });
    if (r.ok) { toast.success('Spremljeno'); setEditing(null); load(); } else toast.error('Greška');
  };

  const remove = async (a) => {
    if (!window.confirm(`Obrisati "${a.name}"?`)) return;
    await authFetch(`${API}/admin/authors/${a.id}`, { method: 'DELETE' });
    toast.success('Obrisano'); load();
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-authors">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <UsersRound className="text-red-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold">Autori</h1>
            <p className="text-sm text-zinc-400">Profili uredništva — prikazani na /autori i uz članke.</p>
          </div>
        </div>
        <button onClick={() => setEditing({ ...empty })} data-testid="new-author-btn"
                className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm font-bold">
          <Plus size={14} /> Novi autor
        </button>
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {authors.map(a => (
            <div key={a.id} className="p-4 rounded-lg border border-zinc-800 bg-zinc-900/50" data-testid={`author-card-${a.id}`}>
              <div className="flex items-center gap-3 mb-2">
                {a.picture ? (
                  <img src={a.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                ) : <div className="w-12 h-12 bg-zinc-800 rounded-full" />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white truncate">{a.name}</p>
                  <p className="text-xs text-zinc-500">{a.role || 'Autor'} · {a.article_count || 0} čl.</p>
                </div>
              </div>
              {a.bio && <p className="text-xs text-zinc-400 line-clamp-2 mb-2">{a.bio}</p>}
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(a)} data-testid={`edit-${a.id}`}
                        className="flex-1 px-2 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 rounded">Uredi</button>
                {!a.auto_seed && (
                  <button onClick={() => remove(a)} data-testid={`del-${a.id}`}
                          className="px-2 py-1 text-xs bg-red-900/40 hover:bg-red-900 rounded text-red-300">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {authors.length === 0 && <p className="col-span-full text-center py-10 text-zinc-500">Nema autora.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-3">{editing.id ? 'Uredi autora' : 'Novi autor'}</h2>
            <div className="space-y-2">
              {[
                ['name', 'Ime', 'text'], ['slug', 'Slug (URL)', 'text'],
                ['email', 'Email', 'email'], ['role', 'Funkcija', 'text'],
                ['picture', 'URL fotografije', 'url'],
              ].map(([k, l, t]) => (
                <div key={k}>
                  <label className="text-xs text-zinc-400">{l}</label>
                  <input type={t} value={editing[k] || ''} onChange={e => setEditing({ ...editing, [k]: e.target.value })}
                         data-testid={`author-field-${k}`}
                         className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs text-zinc-400">Biografija</label>
                <textarea rows={3} value={editing.bio || ''} onChange={e => setEditing({ ...editing, bio: e.target.value })}
                          data-testid="author-field-bio"
                          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-1.5 text-sm" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.visible} onChange={e => setEditing({ ...editing, visible: e.target.checked })} />
                <span>Prikaži na /autori</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-sm">Odustani</button>
              <button onClick={save} data-testid="author-save-btn"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded text-sm font-bold">
                <Save size={12} /> Spremi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
